import React, { useEffect, useState } from 'react';
import CardUtils from '../utils/CardUtils';

/**
 * PointTableModal — Excel-like point table display.
 * Columns: # | Username | Cards / Groups | Score | Total
 * Groups displayed compactly with transparent valid/invalid labels.
 */
export default function PointTableModal({
  show,
  onClose,
  pointTable = [],
  title = 'Point Table',
  message = '',
  gameOver = true,
  countdown,
  headerExtra = null,
  light = false,   // whitish theme (used by the previous-rounds table in the left bar)
  splitOffer = null,        // Pool Rummy prize split offer from the server (cash games / custom rooms)
  currentUserId = null,
  onSplitResponse = null,   // (accept: boolean) => void
  rejoin = null,            // Pool Rummy rejoin window from the server: { deadline, newScore, eligible: [{ userId, currency, amountLocal }] }
  onRejoin = null,          // () => Promise<{ success, error }>  (called after the player confirms)
  rateResult = null,        // Points Rummy: who paid what + what the winner got (set once the hand is settled)
  ratePerPointUSD = null,   // Points Rummy: USD per point
  onPlayAgain = null,       // Points Rummy: () => Promise<{ success, error }> - move straight into the next game
}) {
  // Points Rummy "Play again": one click moves this player into the next game (same room code for custom rooms).
  const [againBusy, setAgainBusy] = useState(false);
  const [againMsg, setAgainMsg] = useState('');
  const doPlayAgain = async () => {
    if (typeof onPlayAgain !== 'function' || againBusy) return;
    setAgainBusy(true);
    setAgainMsg('');
    try {
      const res = await onPlayAgain();
      if (res && res.success === false) setAgainMsg(res.error || 'Could not start a new game');
    } catch (e) {
      setAgainMsg(e.message || 'Could not start a new game');
    } finally {
      setAgainBusy(false);
    }
  };
  const rateMoney = (amount, currency) => `${Number(amount || 0).toFixed(2)} ${currency || 'USD'}`;
  const rateByUser = {};
  if (rateResult && Array.isArray(rateResult.rows)) rateResult.rows.forEach(r => { rateByUser[String(r.userId)] = r; });
  // Rejoin: 10s countdown + confirm step ("are you sure? it costs X and you re-enter on Y points").
  const [rejoinLeft, setRejoinLeft] = useState(null);
  const [rejoinConfirm, setRejoinConfirm] = useState(false);
  const [rejoinBusy, setRejoinBusy] = useState(false);
  const [rejoinMsg, setRejoinMsg] = useState('');
  const rejoinDeadline = rejoin && rejoin.open ? rejoin.deadline : null;
  useEffect(() => {
    if (!rejoinDeadline) { setRejoinLeft(null); setRejoinConfirm(false); return undefined; }
    const tick = () => setRejoinLeft(Math.max(0, Math.ceil((rejoinDeadline - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [rejoinDeadline]);
  const doRejoin = async () => {
    if (typeof onRejoin !== 'function' || rejoinBusy) return;
    setRejoinBusy(true);
    setRejoinMsg('');
    try {
      const res = await onRejoin();
      if (res && res.success === false) setRejoinMsg(res.error || 'Could not rejoin');
    } catch (e) {
      setRejoinMsg(e.message || 'Could not rejoin');
    } finally {
      setRejoinBusy(false);
      setRejoinConfirm(false);
    }
  };
  // Split countdown (seconds left to answer). The next-round timer is held by the server while it is open.
  const [splitLeft, setSplitLeft] = useState(null);
  const splitDeadline = splitOffer && splitOffer.status === 'open' ? splitOffer.deadline : null;
  useEffect(() => {
    if (!splitDeadline) { setSplitLeft(null); return undefined; }
    const tick = () => setSplitLeft(Math.max(0, Math.ceil((splitDeadline - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [splitDeadline]);

  if (!show) return null;

  const splitShares = splitOffer && Array.isArray(splitOffer.shares) ? splitOffer.shares : [];
  const splitByUser = {};
  splitShares.forEach(s => { splitByUser[String(s.userId)] = s; });
  const splitOpen = !!splitOffer && splitOffer.status === 'open';
  const rejoinOpen = !!(rejoin && rejoin.open && (rejoinLeft === null || rejoinLeft > 0));
  const rejoinByUser = {};
  if (rejoinOpen) (rejoin.eligible || []).forEach(e => { rejoinByUser[String(e.userId)] = e; });
  const showRateCol = !!rateResult;
  const showSplitCol = splitShares.length > 0 || rejoinOpen;
  const myShare = currentUserId != null ? splitByUser[String(currentUserId)] : null;
  const iAccepted = !!(myShare && splitOffer && (splitOffer.accepted || []).map(String).includes(String(currentUserId)));
  const money = (amount, currency) => `${Number(amount || 0).toFixed(2)} ${currency || 'USD'}`;

  // Debug: log what data we're getting for each player - DETAILED
  console.log('📊 PointTableModal data:', JSON.stringify(pointTable.map(p => ({
    user: p.username?.substring(0, 6),
    hasGroups: !!(p.groups && (p.groups.sets?.length > 0 || p.groups.runs?.length > 0)),
    setsCount: p.groups?.sets?.length,
    runsCount: p.groups?.runs?.length,
    handCardsCount: p.handCards?.length,
    submitted: p.submitted,
    isDeclarer: p.isDeclarer,
    // DETAILED: Check actual group structure and validation results
    setsCardsTotal: (p.groups?.sets || []).reduce((s, g) => s + (g?.length || 0), 0),
    runsCardsTotal: (p.groups?.runs || []).reduce((s, g) => s + (g?.length || 0), 0),
    groupResultsCount: p.groupResults?.length || 0,
    groupResultsValid: (p.groupResults || []).filter(g => g?.isValid).length,
    groupsStructure: p.groups ? 'exists' : 'null',
  }))));

  const sorted = [...pointTable].sort(
    (a, b) => (a.cumulativeScore || 0) - (b.cumulativeScore || 0)
  );

  const isWaiting = (row) => !row.submitted && !row.dropped && !row.eliminated;

  /**
   * Find cards that are NOT in any group (set or run).
   */
  const getUngroupedCards = (handCards, groups) => {
    if (!handCards || !groups) return handCards || [];
    const groupCardIds = new Set();
    for (const set of (groups.sets || [])) {
      for (const card of set) groupCardIds.add(card.id);
    }
    for (const run of (groups.runs || [])) {
      for (const card of run) groupCardIds.add(card.id);
    }
    return handCards.filter(c => !groupCardIds.has(c.id));
  };

  /** Overlapping card hand — shows all cards side-by-side with LARGE cards */
  const renderHand = (cards, size = 26) => {
    if (!cards || cards.length === 0) return null;
    const overlap = Math.max(10, size - 14);
    const w = size + (cards.length - 1) * overlap;
    const h = size * 1.4;
    return (
      <div style={{ position: 'relative', width: w, height: h, display: 'inline-block', verticalAlign: 'middle' }}>
        {cards.map((c, i) => (
          <div key={c.id || i} style={{
            position: 'absolute', left: i * overlap, top: 0,
            width: size, height: h,
            borderRadius: 4, overflow: 'hidden',
            boxShadow: i === cards.length - 1 ? '0 2px 8px rgba(0,0,0,0.5)' : '0 1px 2px rgba(0,0,0,0.2)',
            zIndex: i,
            border: '1px solid rgba(255,255,255,0.1)',
          }} title={`${c.rank} of ${c.suit}`}>
            <img
              src={CardUtils.getCardImage(c) || '/cards/card_back.png'}
              alt={`${c.rank}${(c.suit || '')[0]}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
            />
          </div>
        ))}
      </div>
    );
  };

  /** Render each group as a small hand row with tiny labels below */
  const renderCompactGroups = (groups, groupResults) => {
      const allGroups = [
      ...(groups?.runs || []).map(r => ({ cards: r, type: 'run' })),
      ...(groups?.sets || []).map(s => ({ cards: s, type: 'set' })),
    ];
    if (allGroups.length === 0) return null;

    const resultsByIndex = {};
    if (groupResults) {
      groupResults.forEach((gr, idx) => { resultsByIndex[idx] = gr; });
    }

    return (
      <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-end', gap: 4 }}>
        {allGroups.map((g, gi) => {
          const gr = resultsByIndex[gi];
          const isValid = gr?.isValid;
          const isPure = gr?.isPure;
          // Determine group type: 'set' or 'sequence'
          const isSet = gr?.type === 'set' || g.type === 'set';
          // 🟢 Pure sequence | 🔵 Set | 🟡 Impure sequence | 🔴 Invalid
          const statusColor = isValid
            ? (isSet ? '#2196F3' : (isPure ? '#4CAF50' : '#FFC107'))
            : '#f44336';
          const bgColor = isValid
            ? (isSet ? 'rgba(33,150,243,0.08)' : (isPure ? 'rgba(76,175,80,0.08)' : 'rgba(255,193,7,0.08)'))
            : 'rgba(244,67,54,0.08)';
          const borderColor = isValid
            ? (isSet ? 'rgba(33,150,243,0.3)' : (isPure ? 'rgba(76,175,80,0.3)' : 'rgba(255,193,7,0.3)'))
            : 'rgba(244,67,54,0.3)';
          // Text for label: SET / PURE / IMPURE / (error reason)
          const labelText = isValid
            ? (isSet ? 'SET' : (isPure ? 'PURE' : 'IMPURE'))
            : (gr?.reason || 'INVALID');
          const titleText = isValid
            ? (isSet ? 'Valid set (same rank, different suits)' : (isPure ? 'Valid pure sequence' : 'Valid impure sequence'))
            : (gr?.reason || 'Invalid group');
          return (
            <div key={gi} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              border: `1px solid ${borderColor}`,
              borderRadius: 6, padding: '3px 4px',
              background: bgColor,
            }}>
              {renderHand(g.cards, 24)}
              <span style={{
                fontSize: 7, fontWeight: 600, padding: '0 3px',
                marginTop: 1, lineHeight: '9px',
                color: statusColor,
                background: 'rgba(0,0,0,0.2)',
                borderRadius: 3,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                maxWidth: '160px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }} title={titleText}>
                {labelText}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Light theme: whitish background with dark text (colours inside the table are kept readable)
  const modalBox = light
    ? { ...modalStyle, backgroundColor: '#f7f8fb', color: '#1b1f2a', boxShadow: '0 8px 40px rgba(0,0,0,0.35)' }
    : modalStyle;
  const lightCss = light ? `
    .pt-light h2 { color: #1b1f2a !important; }
    .pt-light th { color: #55607a !important; border-bottom-color: rgba(0,0,0,0.18) !important; }
    .pt-light td { border-bottom-color: rgba(0,0,0,0.08) !important; color: #1b1f2a; }
    .pt-light td[style*="color: rgb(240, 240, 240)"], .pt-light td[style*="color: rgb(255, 255, 255)"] { color: #1b1f2a !important; }
    .pt-light p { color: #8a6d00 !important; }
    .pt-light span[style*="color: rgb(136, 136, 136)"], .pt-light span[style*="color: rgb(170, 170, 170)"] { color: #6b7280 !important; }
  ` : '';

  return (
    <div style={overlayStyle}>
      {light && <style>{lightCss}</style>}
      <div style={modalBox} className={light ? 'pt-light' : undefined}>
        {/* Header */}
        <div style={light ? { ...headerStyle, borderBottom: '1px solid rgba(0,0,0,0.15)' } : headerStyle}>
          <h2 style={{ margin: 0, color: '#fff', fontSize: 18 }}>{title}</h2>
          {message && <p style={{ margin: '6px 0 0', color: '#ffd700', fontWeight: 600, fontSize: 14 }}>{message}</p>}
          {/* 🕐 COUNTDOWN TIMER — shown when all players submitted and countdown is active */}
          {typeof countdown === 'number' && countdown > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, marginTop: 8,
            }}>
              <div style={{
                background: countdown <= 3 ? 'rgba(244,67,54,0.2)' : 'rgba(76,175,80,0.2)',
                border: countdown <= 3 ? '2px solid #f44336' : '2px solid #4CAF50',
                borderRadius: '50%', width: 48, height: 48,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 22,
                color: countdown <= 3 ? '#f44336' : '#4CAF50',
                animation: countdown <= 3 ? 'pulse-countdown 0.5s ease-in-out infinite' : 'none',
              }}>{countdown}</div>
              <span style={{ color: '#aaa', fontSize: 12 }}>seconds remaining</span>
            </div>
          )}
          {headerExtra}
          {/* 🤝 Pool Rummy split offer status. While it is open the server holds the next-round timer. */}
          {splitOpen && (
            <p style={{ margin: '6px 0 0', color: '#80d8ff', fontWeight: 600, fontSize: 13 }}>
              🤝 Split the prize? All remaining players must agree{typeof splitLeft === 'number' ? ` — ${splitLeft}s to answer` : ''}. The next round is on hold.
            </p>
          )}
          {splitOffer && splitOffer.status === 'rejected' && (
            <p style={{ margin: '6px 0 0', color: '#ff8a80', fontWeight: 600, fontSize: 13 }}>
              ❌ Split not agreed
              {(splitOffer.rejectedBy || []).length > 0 ? ` — not agreed: ${(splitOffer.rejectedBy || []).map(r => r.username).join(', ')}` : ''}
              . The game continues.
            </p>
          )}
          {splitOffer && splitOffer.status === 'agreed' && (
            <p style={{ margin: '6px 0 0', color: '#69f0ae', fontWeight: 600, fontSize: 13 }}>
              ✅ Split agreed — the prize has been added to the wallets.
            </p>
          )}
          {/* 🚀 Auto-transition message when game is not over */}
          {!gameOver && !splitOpen && typeof countdown === 'number' && countdown === 0 && (
            <p style={{ margin: '6px 0 0', color: '#4CAF50', fontWeight: 600, fontSize: 13 }}>
              🚀 Starting next deal...
            </p>
          )}
        </div>

        {/* Excel-like Table */}
        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 32 }}>#</th>
                <th style={{ ...thStyle, textAlign: 'left', minWidth: 120 }}>Username</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Cards / Groups</th>
                <th style={{ ...thStyle, width: 52 }}>Score</th>
                <th style={{ ...thStyle, width: 60 }}>Total</th>
                {showSplitCol && <th style={{ ...thStyle, width: 150 }}>Split prize</th>}
                {showRateCol && <th style={{ ...thStyle, width: 150 }}>Cash</th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, index) => {
                const isWinner = index === 0 && gameOver;
                const rowBg = isWaiting(row) ? 'rgba(255,193,7,0.06)'
                  : row.eliminated || row.dropped ? 'rgba(255,0,0,0.05)'
                  : isWinner ? 'rgba(0,255,0,0.08)'
                  : 'transparent';

                return (
                  <tr key={row.userId} style={{ background: rowBg }}>
                    {/* # */}
                    <td style={{ ...tdStyle, color: isWinner ? '#4CAF50' : '#888', fontWeight: isWinner ? 700 : 400 }}>
                      {index + 1}
                      {isWinner && ' 🏆'}
                    </td>
                    {/* Username */}
                    <td style={{ ...tdStyle, textAlign: 'left', color: isWinner ? '#4CAF50' : '#f0f0f0', fontWeight: isWinner ? 700 : 500 }}>
                      {row.username}
                      {row.isDeclarer && !isWinner && (
                        <span style={{ fontSize: 10, color: '#FFC107', marginLeft: 4 }}>(D)</span>
                      )}
                      {row.wrongDeclaration && (
                        <span style={{ fontSize: 10, color: '#FF5722', marginLeft: 4, fontWeight: 700, background: 'rgba(244,67,54,0.2)', padding: '1px 4px', borderRadius: 3 }}>❌ Wrong Declaration</span>
                      )}
                      {row.eliminated && (
                        <span style={{ fontSize: 10, color: '#f44336', marginLeft: 4 }}>Eliminated</span>
                      )}
                      {row.dropped && !row.eliminated && !row.wrongDeclaration && (
                        <span style={{ fontSize: 10, color: '#999', marginLeft: 4 }}>Dropped</span>
                      )}
                    </td>
                    {/* Cards / Groups — Show as ARRANGED HAND */}
                    <td style={{ ...tdStyle, textAlign: 'left', padding: '10px 12px' }}>
                      {isWaiting(row) ? (
                        <span style={{ color: '#FFC107', fontStyle: 'italic', fontSize: 12 }}>⏳ Submitting...</span>
                      ) : (() => {
                        const hasGroups = row.groups && (row.groups.sets?.length > 0 || row.groups.runs?.length > 0);
                        const ungrouped = getUngroupedCards(row.handCards, row.groups);
                        if (hasGroups) {
                          return (
                            <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-end', gap: 8 }}>
                              {renderCompactGroups(row.groups, row.groupResults)}
                              {ungrouped.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                                  <span style={{ fontSize: 8, color: '#ff9800', fontWeight: 600 }} title="Ungrouped cards">UG</span>
                                  {renderHand(ungrouped, 26)}
                                  <span style={{ fontSize: 9, color: '#888' }}>({ungrouped.length})</span>
                                </div>
                              )}
                            </div>
                          );
                        } else {
                          return Array.isArray(row.handCards) && row.handCards.length > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
                              {renderHand(row.handCards, 26)}
                              <span style={{ fontSize: 9, color: '#888', whiteSpace: 'nowrap' }}>({row.handCards.length})</span>
                            </div>
                          ) : (
                            <span style={{ color: '#666', fontSize: 11 }}>—</span>
                          );
                        }
                      })()}
                    </td>
                    {/* Score */}
                    <td style={{
                      ...tdStyle, fontWeight: 600, fontSize: 14,
                      color: isWaiting(row) ? '#FFC107'
                        : (row.pointsThisGame ?? 0) > 0 ? '#FF5722'
                        : (row.pointsThisGame === 0) ? '#4CAF50' : '#aaa',
                    }}>
                      {isWaiting(row) ? '⏳' : (row.pointsThisGame !== null && row.pointsThisGame !== undefined ? row.pointsThisGame : '—')}
                    </td>
                    {/* Total — Show cumulative score with per-deal points breakdown */}
                    <td style={{ ...tdStyle, fontWeight: 600, fontSize: 14, color: '#fff' }}>
                      {row.cumulativeScore ?? 0}
                      {row.pointsThisGame !== null && row.pointsThisGame !== undefined && (
                        <span style={{ fontSize: 10, color: '#aaa', marginLeft: 4, fontWeight: 400 }}>
                          ({row.pointsThisGame > 0 ? '+' : ''}{row.pointsThisGame})
                        </span>
                      )}
                    </td>
                    {/* 🎯 Points Rummy: what each player paid (points x amount) / what the winner received */}
                    {showRateCol && (() => {
                      const r = rateByUser[String(row.userId)];
                      if (!r) return <td style={{ ...tdStyle, color: '#666', fontSize: 11 }}>—</td>;
                      const isMe = currentUserId != null && String(row.userId) === String(currentUserId);
                      if (r.isWinner) {
                        const won = rateResult.winnerPayout;
                        return (
                          <td style={{ ...tdStyle, padding: '4px 6px' }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#69f0ae', whiteSpace: 'nowrap' }}>
                              + {won ? rateMoney(won.amount, won.currency) : rateMoney(0, r.currency)}{isMe ? ' ⬅' : ''}
                            </div>
                            <div style={{ fontSize: 9, color: '#9aa' }}>winner</div>
                          </td>
                        );
                      }
                      return (
                        <td style={{ ...tdStyle, padding: '4px 6px' }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#ff8a80', whiteSpace: 'nowrap' }}>
                            − {rateMoney(r.paidLocal, r.currency)}{isMe ? ' ⬅' : ''}
                          </div>
                          <div style={{ fontSize: 9, color: '#9aa' }}>
                            {r.points} pts{ratePerPointUSD ? ` × ${ratePerPointUSD} USD` : ''}{r.short ? ' · wallet was short' : ''}
                          </div>
                        </td>
                      );
                    })()}
                    {/* 🤝 Split prize for the round + Agree / Disagree (only the remaining players take part) */}
                    {showSplitCol && (() => {
                      const share = splitByUser[String(row.userId)];
                      const rj = rejoinByUser[String(row.userId)];
                      const rejoinedNow = rejoinOpen && (rejoin.rejoined || []).map(String).includes(String(row.userId));
                      if (!share && rj) {
                        const isMeRj = currentUserId != null && String(row.userId) === String(currentUserId);
                        const cost = rj.amountLocal != null ? money(rj.amountLocal, rj.currency) : null;
                        return (
                          <td style={{ ...tdStyle, padding: '4px 6px' }}>
                            <div style={{ fontSize: 10, color: '#ffcc80', whiteSpace: 'nowrap' }}>Eliminated</div>
                            {isMeRj && typeof onRejoin === 'function' ? (
                              rejoinConfirm ? (
                                <div style={{ marginTop: 3 }}>
                                  <div style={{ fontSize: 10, color: '#fff', lineHeight: 1.3 }}>
                                    Rejoin on <b>{rejoin.newScore}</b> pts{cost ? <> for <b>{cost}</b></> : null}?
                                  </div>
                                  <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 3 }}>
                                    <button disabled={rejoinBusy} onClick={doRejoin}
                                      style={{ ...primaryBtnStyle, padding: '3px 10px', fontSize: 11, borderRadius: 6 }}>{rejoinBusy ? '...' : 'Yes'}</button>
                                    <button disabled={rejoinBusy} onClick={() => setRejoinConfirm(false)}
                                      style={{ ...secondaryBtnStyle, padding: '3px 8px', fontSize: 11, borderRadius: 6 }}>No</button>
                                  </div>
                                </div>
                              ) : (
                                <button onClick={() => { setRejoinMsg(''); setRejoinConfirm(true); }}
                                  style={{ ...primaryBtnStyle, padding: '3px 10px', fontSize: 11, borderRadius: 6, marginTop: 3 }}>
                                  🔁 Rejoin{typeof rejoinLeft === 'number' ? ` (${rejoinLeft}s)` : ''}
                                </button>
                              )
                            ) : (
                              <div style={{ fontSize: 10, color: '#9aa', marginTop: 2 }}>can rejoin</div>
                            )}
                            {isMeRj && rejoinMsg && <div style={{ fontSize: 10, color: '#ff8a80', marginTop: 2 }}>{rejoinMsg}</div>}
                          </td>
                        );
                      }
                      if (!share && rejoinedNow) return <td style={{ ...tdStyle, color: '#69f0ae', fontSize: 11 }}>🔁 rejoined</td>;
                      if (!share) return <td style={{ ...tdStyle, color: '#666', fontSize: 11 }}>—</td>;
                      const isMe = currentUserId != null && String(row.userId) === String(currentUserId);
                      const agreed = (splitOffer.accepted || []).map(String).includes(String(row.userId));
                      const refused = (splitOffer.rejectedBy || []).some(r => String(r.userId) === String(row.userId));
                      const mine = isMe ? ' ⬅' : '';
                      return (
                        <td style={{ ...tdStyle, padding: '4px 6px' }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#69f0ae', whiteSpace: 'nowrap' }}>
                            {money(share.amountLocal != null ? share.amountLocal : share.amountUSD, share.currency)}
                          </div>
                          <div style={{ fontSize: 9, color: '#9aa', whiteSpace: 'nowrap' }}>
                            {share.remainingPoints} left · {Math.round((share.percent || 0) * 100)}%
                          </div>
                          {splitOpen && isMe && !iAccepted && typeof onSplitResponse === 'function' && (
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 3 }}>
                              <button onClick={() => onSplitResponse(true)}
                                style={{ ...primaryBtnStyle, padding: '3px 10px', fontSize: 11, borderRadius: 6 }}>Agree</button>
                              <button onClick={() => onSplitResponse(false)}
                                style={{ ...secondaryBtnStyle, padding: '3px 8px', fontSize: 11, borderRadius: 6 }}>No</button>
                            </div>
                          )}
                          {splitOpen && agreed && <div style={{ fontSize: 10, color: '#69f0ae', marginTop: 2 }}>✔ agreed{mine}</div>}
                          {splitOpen && !agreed && !(isMe && typeof onSplitResponse === 'function') && (
                            <div style={{ fontSize: 10, color: '#FFC107', marginTop: 2 }}>⏳ waiting</div>
                          )}
                          {splitOffer.status === 'rejected' && refused && (
                            <div style={{ fontSize: 10, color: '#ff8a80', marginTop: 2 }}>✖ not agreed</div>
                          )}
                        </td>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Buttons */}
        <div style={buttonRowStyle}>
          {typeof onPlayAgain === 'function' && gameOver && (
            <button style={primaryBtnStyle} disabled={againBusy} onClick={doPlayAgain}>
              {againBusy ? '...' : '🔁 Play again'}
            </button>
          )}
          {againMsg && <span style={{ color: '#ff8a80', fontSize: 12, alignSelf: 'center' }}>{againMsg}</span>}
          <button style={secondaryBtnStyle} onClick={onClose}>
            {gameOver ? 'Close' : 'Continue Watching'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== STYLES ====================

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.75)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 10000,
};
// Compact layout: every player's result fits one window. The wrapper only scrolls as a last resort
// (very small screens); cards sit on a single line instead of wrapping into two rows.
const modalStyle = {
  backgroundColor: '#1a1a2e', borderRadius: 14, padding: '12px 14px',
  maxWidth: 1000, width: '97vw', maxHeight: '96vh',
  overflow: 'hidden', display: 'flex', flexDirection: 'column',
  boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
};
const headerStyle = {
  textAlign: 'center', marginBottom: 6, paddingBottom: 6,
  borderBottom: '1px solid rgba(255,255,255,0.15)',
};
const tableWrapperStyle = {
  overflowY: 'auto', overflowX: 'auto', flex: '0 1 auto', padding: '2px 0',
};
const tableStyle = {
  width: '100%', borderCollapse: 'collapse',
  fontSize: 13,
};
const thStyle = {
  padding: '5px 6px',
  borderBottom: '2px solid rgba(255,255,255,0.2)',
  color: '#888', fontWeight: 600, fontSize: 12,
  textAlign: 'center', whiteSpace: 'nowrap',
  textTransform: 'uppercase', letterSpacing: '0.5px',
};
const tdStyle = {
  padding: '5px 6px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  textAlign: 'center', verticalAlign: 'middle',
};
const buttonRowStyle = {
  display: 'flex', justifyContent: 'center', gap: 12,
  marginTop: 6, paddingTop: 6,
  borderTop: '1px solid rgba(255,255,255,0.15)',
};
const primaryBtnStyle = {
  padding: '8px 24px', borderRadius: 8, border: 'none',
  backgroundColor: '#4CAF50', color: '#fff',
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const secondaryBtnStyle = {
  padding: '8px 24px', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.3)',
  backgroundColor: 'transparent', color: '#ccc',
  fontSize: 14, cursor: 'pointer',
};
